import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
/** An API failure, carrying the server's status and message. */
export class ExecDashboardError extends Error {
    constructor(message, status) {
        super(message);
        this.status = status;
        this.name = 'ExecDashboardError';
    }
}
async function unwrap(res, fallback) {
    if (res.success && res.data)
        return res.data;
    throw new ExecDashboardError(res.error || fallback, res.status);
}
export const EXEC_REFRESH_MS = 60000;
/** Figures are cached 60 s on the server; a younger answer is not refetched. */
const STALE_MS = 30000;
export const execQueryKeys = {
    all: ['executive'],
    dashboard: (q) => ['executive', 'dashboard', q],
    /**
     * `scope` is the work-context account when the server answers for it (the
     * query names no account): two accounts' answers must never share an entry.
     */
    pages: (q, scope = null) => ['executive', 'pages', q, scope],
    detail: (locationId, q) => ['executive', 'detail', locationId, q],
    /** Every period of one location's detail (prefix of `detail`). */
    detailAll: (locationId) => ['executive', 'detail', locationId],
};
/** The account a `pages` key answers for: the one its query names, else its scope (null: none). */
const pagesKeyAccount = (key) => {
    const q = key[2];
    return q?.account_id ?? key[3] ?? null;
};
/** Don't retry what the server refused (403/404): it will refuse again. */
function retry(count, error) {
    const status = error instanceof ExecDashboardError ? error.status : undefined;
    if (status && status >= 400 && status < 500)
        return false;
    return count < 2;
}
/** One page of the dashboard. */
export function useExecutiveDashboardQuery(service, query, options = {}) {
    return useQuery({
        queryKey: execQueryKeys.dashboard(query),
        queryFn: async () => unwrap(await service.getDashboard(query), 'Could not load the dashboard'),
        placeholderData: keepPreviousData,
        staleTime: STALE_MS,
        refetchInterval: options.refetchIntervalMs ?? EXEC_REFRESH_MS,
        enabled: options.enabled ?? true,
        retry,
    });
}
/**
 * The dashboard page by page ("Show more"): the rows of every loaded page,
 * each row once, with the first page's totals.
 */
export function useExecutiveDashboardPages(service, query, options = {}) {
    const pageSize = options.pageSize ?? query.page_size ?? 25;
    const q = { ...query, page_size: pageSize };
    const scope = options.scope ?? null;
    const account = q.account_id ?? scope;
    const result = useInfiniteQuery({
        queryKey: execQueryKeys.pages(q, scope),
        queryFn: async ({ pageParam }) => unwrap(await service.getDashboard({ ...q, page: pageParam }), 'Could not load the dashboard'),
        initialPageParam: 1,
        getNextPageParam: (last) => (last.page * last.page_size < last.total ? last.page + 1 : undefined),
        // The previous answer stays up while the next loads — never another account's.
        placeholderData: (previous, previousQuery) => previousQuery && pagesKeyAccount(previousQuery.queryKey) === account ? previous : undefined,
        staleTime: STALE_MS,
        refetchInterval: options.refetchIntervalMs ?? EXEC_REFRESH_MS,
        enabled: options.enabled ?? true,
        retry,
    });
    const pages = result.data?.pages ?? [];
    const seen = new Set();
    const rows = [];
    for (const p of pages) {
        for (const r of p.rows) {
            const key = `${r.kind}:${r.id}`;
            // A refetch between pages can shift rows; never show one twice.
            if (!seen.has(key)) {
                seen.add(key);
                rows.push(r);
            }
        }
    }
    return { ...result, first: pages[0] ?? null, rows };
}
/** One location's detail, loaded only while it is open. */
export function useExecutiveLocationDetail(service, locationId, query) {
    return useQuery({
        queryKey: execQueryKeys.detail(locationId ?? '', query),
        queryFn: async () => unwrap(await service.getLocationDetail(locationId, query), 'Could not load the location'),
        enabled: !!locationId,
        // No placeholder: another location's figures must never show under this
        // one's name while it loads.
        staleTime: STALE_MS,
        refetchInterval: EXEC_REFRESH_MS,
        retry,
    });
}
